// frontend/src/components/SchoolSwitcher.tsx
/**
 * SchoolSwitcher — header element showing the user's active institution.
 *
 * Single-membership users see a static badge with the school's short name
 * (e.g., "UMD"). Multi-membership users see a dropdown listing every
 * school they belong to; selecting one calls `setActive(id)` from
 * ActiveInstitutionContext, which:
 *
 *   1. Persists the choice via POST /api/institutions/me/active
 *   2. Updates the apiClient's X-Institution-Slug header (so subsequent
 *      requests scope to the new school)
 *   3. Triggers InstitutionConfigContext to re-fetch the institution
 *      payload (per-school theme, data sources, GE areas), and AppContext
 *      to re-sync the dbUser cache under the new slug key
 *
 * Pre-membership state (zero memberships): falls back to the host-derived
 * theme badge — preserves the pre-E1 single-tenant rendering when the
 * /api/institutions/me request hasn't resolved yet or returned empty.
 *
 * Phase E4 of the multi-membership architecture plan
 * (docs/plans/multi-institution/11-multi-membership-architecture.md).
 */

import { useActiveInstitution } from '../contexts/ActiveInstitutionContext';
import { useInstitutionTheme } from '../contexts/InstitutionConfigContext';
import { resolveTheme } from '../themes';
import { InstitutionDropdown } from './shared/InstitutionDropdown';
import { controlClasses } from './ui';

export const SchoolSwitcher = () => {
  const theme = useInstitutionTheme();
  const { memberships, active, setActive } = useActiveInstitution();

  // Single-membership (or pre-resolution) — render the static badge.
  // Identical to the pre-Phase-E4 behavior so UMD-only users see no change.
  if (memberships.length <= 1) {
    return (
      <div
        // A static badge, but it stands in the header control row, so it takes
        // the same height contract as its neighbours (ui/control.ts).
        className={controlClasses(
          'md',
          'px-3 bg-primary/10 text-primary text-sm font-semibold select-none'
        )}
        title={theme.universityName}
        aria-label={`Current institution: ${theme.universityName}`}
      >
        {theme.universityShortName}
      </div>
    );
  }

  const activeLabel = active
    ? resolveTheme(active.institution.slug).universityShortName
    : theme.universityShortName;
  const activeFullName = active
    ? resolveTheme(active.institution.slug).universityName
    : theme.universityName;

  return (
    <InstitutionDropdown
      memberships={memberships}
      active={active}
      onSelect={(institutionId) => setActive(institutionId)}
      testIdPrefix="school-switcher"
      containerClassName="relative"
      listClassName="absolute left-0 md:left-auto md:right-0 mt-2 min-w-[200px] max-w-[calc(100vw-1.5rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50"
      itemClassName={({ isActive }) =>
        `w-full text-left px-3 py-2 text-sm transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary font-semibold cursor-default'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
        }`
      }
      renderTrigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          className={controlClasses(
            'md',
            'gap-1.5 px-3 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20'
          )}
          title={activeFullName}
          aria-label={`Switch institution. Current: ${activeFullName}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="school-switcher-trigger"
        >
          <span>{activeLabel}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
      renderItem={({ membership, isActive }) => {
        const itemTheme = resolveTheme(membership.institution.slug);
        return (
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span>{itemTheme.universityShortName}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                {itemTheme.universityName}
              </span>
            </div>
            {isActive && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        );
      }}
    />
  );
};
